import React, { useEffect, useState } from 'react';
import api from '../../api';
import AdminLayout from './AdminLayout';
import { Card } from '../ui/card';
import { Users, IndianRupee, Car, ClipboardCheck, Clock, XCircle } from 'lucide-react';
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';

const CARD_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444'];

function Stat({ icon: Icon, label, value, sub, color = 'text-primary' }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/api/admin/stats');
        setStats(data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Failed to load stats');
      }
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  if (error) {
    return (
      <AdminLayout>
        <div className="text-red-600">Error: {String(error)}</div>
      </AdminLayout>
    );
  }
  if (!stats) {
    return (
      <AdminLayout>
        <div className="text-muted-foreground">Loading…</div>
      </AdminLayout>
    );
  }

  const bookingStatusData = [
    { name: 'Completed', value: stats.bookings.completed },
    { name: 'Cancelled', value: stats.bookings.cancelled },
    { name: 'In progress', value: stats.bookings.in_progress },
  ];

  const driversData = [
    { name: 'Approved', value: stats.drivers.approved },
    { name: 'Pending',  value: stats.drivers.pending },
    { name: 'Rejected', value: stats.drivers.rejected },
  ];

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Overview</h1>
          <p className="text-muted-foreground text-sm">Real-time platform snapshot</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={Users} label="Users" value={stats.users_count} data-testid="stat-users" color="text-primary" />
        <Stat icon={Car} label="Approved Drivers" value={stats.drivers.approved} color="text-green-600" />
        <Stat icon={Clock} label="Pending Drivers" value={stats.drivers.pending} color="text-amber-600" />
        <Stat icon={XCircle} label="Rejected Drivers" value={stats.drivers.rejected} color="text-red-600" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={ClipboardCheck} label="Bookings (total)" value={stats.bookings.total} color="text-foreground" />
        <Stat icon={IndianRupee} label="Revenue today" value={`₹${stats.revenue.today}`} color="text-green-600" />
        <Stat icon={IndianRupee} label="Revenue (7d)" value={`₹${stats.revenue.week}`} color="text-primary" />
        <Stat icon={IndianRupee} label="Revenue (all time)" value={`₹${stats.revenue.total}`} color="text-accent-dark" sub="Only completed trips" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Bookings — last 7 days</h3>
          {stats.daily.length === 0 ? (
            <p className="text-sm text-muted-foreground">No bookings in the last 7 days.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={stats.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="bookings" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold mb-4">Revenue — last 7 days</h3>
          {stats.daily.length === 0 ? (
            <p className="text-sm text-muted-foreground">No revenue yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip />
                <Bar dataKey="revenue" fill="#16a34a" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Bookings by status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={bookingStatusData} innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value">
                {bookingStatusData.map((_, i) => (
                  <Cell key={i} fill={CARD_COLORS[i % CARD_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold mb-4">Drivers by status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={driversData} innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value">
                {driversData.map((_, i) => (
                  <Cell key={i} fill={CARD_COLORS[i % CARD_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </AdminLayout>
  );
}
